import cn from 'classnames';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { SortDirection } from '../../utils/tableHelper';
import { ChevronCaret } from '../icons/analytics/ChevronCaret';
import { cymbioColors } from '../../utils/colors';
import styles from './TableCells.module.scss';
import { InfoTitle } from '../common/InfoTooltip/InfoTitle';

export function TableHeader({
  columns,
  overrides,
  columnsData,
  sort: { sortedBy, direction, onSortBy, initialColumn },
}) {
  const displayedColumns = overrides ? overrides(columns, columnsData) : columns;

  const handleSortBy = (column) => {
    if (column === sortedBy) {
      if (direction === SortDirection.ASC) {
        column = initialColumn;
        direction = SortDirection.DESC;
      } else {
        direction = SortDirection.ASC;
      }
    } else {
      direction = SortDirection.DESC;
    }

    onSortBy(column, direction);
  };

  const getIconClasses = useCallback(
    (sortKey) => {
      const classes = [styles.sortIcon];
      if (sortedBy === sortKey) {
        classes.push(direction === SortDirection.ASC ? styles.ascending : styles.descending);
      }
      return cn(classes);
    },
    [direction, sortedBy],
  );

  const getWrapperClasses = useCallback(
    (sortKey, canBeSorted) => {
      const classes = [styles.wrapper];
      if (canBeSorted) {
        classes.push(styles.sortable);
        if (sortedBy === sortKey) {
          classes.push(styles.sortedBy);
        }
      }
      return cn(classes);
    },
    [sortedBy],
  );

  return (
    <thead>
      <tr>
        {displayedColumns.map((column) => {
          const columnData = columnsData[column];
          const canBeSorted = columnData?.canBeSorted;
          const sortKey = columnData.sortKey ? columnData.sortKey : column;

          return (
            <th key={column} className={cn(styles.tableHeaderCell, columnData.styleFunction?.())}>
              <button
                className={getWrapperClasses(sortKey, canBeSorted)}
                onClick={() => (canBeSorted ? handleSortBy(sortKey) : null)}
              >
                {columnData.tooltip ? (
                  <InfoTitle content={<div className={styles.tooltip}>{columnData.tooltip}</div>}>
                    <span>{columnData.label} </span>
                  </InfoTitle>
                ) : (
                  columnData.label + ' '
                )}

                {canBeSorted && (
                  <ChevronCaret
                    color={sortedBy === sortKey ? cymbioColors.analytics.textPrimary : undefined}
                    className={getIconClasses(sortKey)}
                  />
                )}
              </button>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

TableHeader.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  columnsData: PropTypes.object.isRequired,
  overrides: PropTypes.func,
  sort: PropTypes.shape({
    sortedBy: PropTypes.string,
    direction: PropTypes.string,
    onSortBy: PropTypes.func.isRequired,
    initialColumn: PropTypes.string,
  }).isRequired,
};
